import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { FormResultForEmployeeModal } from "@/components/form-result/result-modal/FormResultForEmployeeModal.tsx";
import { FormParticipantStatusTag } from "@/components/forms/components/FormParticipantStatusTag.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { HistoryTable } from "@/pages/employees/employee/tables/HistoryTable.tsx";
import { ScoreTag } from "@/pages/talent/performance-reviews/view/components/ScoreTag.tsx";
import { useSearchEmployeeReviewParticipantViews } from "@/queries/employees/use-employee-performance-review.query.ts";
import { useSearchEmployeeSurveyParticipantViews } from "@/queries/employees/use-employee-survey.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiFormParticipantStatus, ApiFormType } from "@/types/forms/form.types.ts";
import {
  ApiPerformanceReviewParticipantView,
  ApiPerformanceReviewParticipantViewFilter,
} from "@/types/performance-review.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { ApiSurveyParticipantView, ApiSurveyParticipantViewFilter } from "@/types/survey.types.ts";
import {
  DropdownMenu,
  DropdownMenuItem,
  ListItem,
  Stack,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, DisplayColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export function EmployeeFeedbackEvaluation({ employee }: EmployeePageComponentProps) {
  const { t } = useTranslation();
  const { renderDate } = useFormatter();
  const { canIEdit, isFeatureEnabled } = usePermissions();
  const { openModal } = useModals();

  const [reviewFilter, setReviewFilter] = useState<ApiPerformanceReviewParticipantViewFilter>({
    page_number: 1,
  });
  const [surveyFilter, setSurveyFilter] = useState<ApiSurveyParticipantViewFilter>({
    page_number: 1,
  });

  const canViewPerformanceReviews =
    isFeatureEnabled(ApiFeatureType.talent_performance_reviews) &&
    canIEdit(ApiPermission.performance_reviews_module);
  const canViewSurveys =
    isFeatureEnabled(ApiFeatureType.talent_surveys) && canIEdit(ApiPermission.surveys_module);

  const {
    data: reviewParticipantViews,
    isLoading: isReviewsLoading,
    isFetching: isReviewsFetching,
  } = useSearchEmployeeReviewParticipantViews({
    employeeId: employee.id,
    filter: reviewFilter,
    enabled: canViewPerformanceReviews,
  });

  const {
    data: surveyParticipantViews,
    isLoading: isSurveysLoading,
    isFetching: isSurveysFetching,
  } = useSearchEmployeeSurveyParticipantViews({
    employeeId: employee.id,
    filter: surveyFilter,
    enabled: canViewSurveys,
  });

  const handleOpenSurveyAction = useCallback(
    (item: ApiSurveyParticipantView) => {
      openModal(FormResultForEmployeeModal, {
        employeeId: employee.id,
        questionnaireId: item.survey.id,
        type: ApiFormType.survey,
      });

      analyticsService.trackEvent(analyticEvents.surveys.participantResultViewed, {
        [analyticProperties.id]: employee.id,
        [analyticProperties.source]: item.survey.id,
      });
    },
    [employee.id, openModal]
  );

  const reviewColumnHelper = createColumnHelper<ApiPerformanceReviewParticipantView>();
  const reviewColumns = useMemo<ColumnDef<ApiPerformanceReviewParticipantView>[]>(() => {
    const inner: DisplayColumnDef<ApiPerformanceReviewParticipantView>[] = [];
    inner.push(
      reviewColumnHelper.display({
        id: "date",
        header: t("common:date"),
        cell: (cell) => (
          <>
            {cell.row.original.review.date_completed_at ? (
              <Text>{renderDate(cell.row.original.review.date_completed_at)}</Text>
            ) : (
              "-"
            )}
          </>
        ),
        ...ColumnSizes.date,
      })
    );
    inner.push(
      reviewColumnHelper.display({
        id: "performance_review",
        header: t("common:title"),
        cell: (cell) => (
          <ListItem
            emoji={cell.row.original.review.emoji}
            title={cell.row.original.review.title}
            forceShowAvatar={true}
            truncate={true}
            behavior={"card"}
          />
        ),
        ...ColumnSizes.wide,
      })
    );
    inner.push(
      reviewColumnHelper.display({
        id: "score",
        header: t("performance_review:score"),
        cell: (cell) => (
          <Stack direction={"horizontal"}>
            <ScoreTag score={cell.row.original.scale_score.avg_scale_score} />
          </Stack>
        ),
        ...ColumnSizes.narrow,
      })
    );
    return inner;
  }, [renderDate, reviewColumnHelper, t]);

  const surveyColumnHelper = createColumnHelper<ApiSurveyParticipantView>();
  const surveyColumns = useMemo<ColumnDef<ApiSurveyParticipantView>[]>(() => {
    const inner: DisplayColumnDef<ApiSurveyParticipantView>[] = [];
    inner.push(
      surveyColumnHelper.display({
        id: "date",
        header: t("common:date"),
        cell: (cell) => {
          const surveyDate = cell.row.original.datetime_completed_at;

          return <Text>{surveyDate ? renderDate(surveyDate) : "-"}</Text>;
        },
        ...ColumnSizes.date,
      })
    );
    inner.push(
      surveyColumnHelper.display({
        id: "survey",
        header: t("common:title"),
        cell: (cell) => (
          <ListItem
            emoji={cell.row.original.survey.emoji}
            title={cell.row.original.survey.title}
            forceShowAvatar={true}
            truncate={true}
            behavior={"card"}
          />
        ),
        ...ColumnSizes.wide,
      })
    );
    inner.push(
      surveyColumnHelper.display({
        id: "participant_status",
        header: t("common:status"),
        cell: (cell) => (
          <Stack direction={"horizontal"}>
            {cell.row.original.participant_status ? (
              <FormParticipantStatusTag status={cell.row.original.participant_status} />
            ) : (
              <Text>{t("common:anonymous")}</Text>
            )}
          </Stack>
        ),
        ...ColumnSizes.status,
      })
    );
    return inner;
  }, [renderDate, surveyColumnHelper, t]);

  const renderReviewRowAction = useCallback(
    (row: Row<ApiPerformanceReviewParticipantView>) => (
      <DropdownMenu>
        <DropdownMenuItem
          to={`/performance-reviews/${row.original.review.id}/view/participants/${employee.id}`}
          target={"_blank"}
          title={t("common:view")}
        />
      </DropdownMenu>
    ),
    [employee.id, t]
  );

  const renderSurveyRowAction = useCallback(
    (row: Row<ApiSurveyParticipantView>) => {
      if (
        row.original.participant_status !== ApiFormParticipantStatus.completed ||
        !row.original.acl.can_view
      ) {
        return null;
      }
      return (
        <DropdownMenu>
          <DropdownMenuItem
            onClick={() => handleOpenSurveyAction(row.original)}
            title={t("common:view")}
          />
        </DropdownMenu>
      );
    },
    [handleOpenSurveyAction, t]
  );

  const reviewsCount = reviewParticipantViews?.metadata.total_items ?? 0;
  const surveysCount = surveyParticipantViews?.metadata.total_items ?? 0;

  return (
    <Stack gap="xl">
      {canViewPerformanceReviews && (
        <Stack gap="lg">
          <Stack className={"justify-between"} direction={"horizontal"} items={"center"} gap={"md"}>
            <Title
              header={t("performance_review:performance_reviews")}
              size={"lg"}
              badge={reviewsCount > 0 ? `(${reviewsCount})` : undefined}
              badgeVariant={"secondary"}
            />
          </Stack>

          <HistoryTable<ApiPerformanceReviewParticipantView>
            historyItems={reviewParticipantViews?.items}
            columns={reviewColumns}
            canViewHistory={true}
            defaultVisibleRecords={4}
            isLoading={isReviewsLoading}
            isFetching={isReviewsFetching}
            pagination={{
              metadata: reviewParticipantViews?.metadata,
              currentPage: reviewFilter.page_number ?? 1,
              onPageChange: (page) => {
                setReviewFilter((currentFilter) => ({ ...currentFilter, page_number: page }));
              },
            }}
            emptyStateTitle={t("performance_review:employee_performance_empty_state_title")}
            notFoundComponent={<EmptyStateNothingFound />}
            stickyLastColumn={false}
            renderRowAction={renderReviewRowAction}
            onRowClick={(row) =>
              window.open(
                `/performance-reviews/${row.original.review.id}/view/participants/${employee.id}`,
                "_blank"
              )
            }
          />
        </Stack>
      )}

      {canViewSurveys && (
        <Stack gap="lg">
          <Stack className={"justify-between"} direction={"horizontal"} items={"center"} gap={"md"}>
            <Title
              header={t("surveys:surveys")}
              size={"lg"}
              badge={surveysCount > 0 ? `(${surveysCount})` : undefined}
              badgeVariant={"secondary"}
            />
          </Stack>

          <HistoryTable<ApiSurveyParticipantView>
            historyItems={surveyParticipantViews?.items}
            columns={surveyColumns}
            canViewHistory={true}
            defaultVisibleRecords={4}
            isLoading={isSurveysLoading}
            isFetching={isSurveysFetching}
            pagination={{
              metadata: surveyParticipantViews?.metadata,
              currentPage: surveyFilter.page_number ?? 1,
              onPageChange: (page) => {
                setSurveyFilter((currentFilter) => ({ ...currentFilter, page_number: page }));
              },
            }}
            emptyStateTitle={t("surveys:employee_surveys_empty_state_title")}
            notFoundComponent={<EmptyStateNothingFound />}
            stickyLastColumn={false}
            renderRowAction={renderSurveyRowAction}
            onRowClick={(row) => {
              if (
                row.original.participant_status === ApiFormParticipantStatus.completed &&
                row.original.acl.can_view
              ) {
                handleOpenSurveyAction(row.original);
              }
            }}
          />
        </Stack>
      )}
    </Stack>
  );
}
